import type { FleetConsumptionReport } from "@station/contracts";
import { toIstanbulDate } from "@station/core";
import {
  Alert,
  Card,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  MoneyText,
  QuantityText,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FileSpreadsheet } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MaskedNotice, PersonalValue } from "../../../features/customers/personal-data.js";
import { formatPlateDisplay } from "../../../features/customers/personal-data-values.js";
import { consumptionText, defaultConsumptionRange } from "../../../features/fleet/fleet-values.js";
import { useFleetAccounts, useFleetConsumption } from "../../../features/fleet/queries.js";
import { FleetPage } from "../../../features/fleet/shared.js";
import { moneyExport } from "../../../features/market/market-values.js";
import { Field } from "../../../features/market/shared.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatUnitPrice } from "../../../lib/format.js";

export const Route = createFileRoute("/_app/filo/tuketim")({ component: ConsumptionRoute });

type Row = FleetConsumptionReport["items"][number];

/**
 * Filo tüketim raporu (Prompt 17 web): araç bazında litre, tutar, işlem, ortalama birim fiyat ve L/100 km (doludan doluya:
 * km'ye göre sıralı dolumlarda ilk dolum dışarıda kalır). Araçsız kullanım ayrı satırdır. Excel'e aktarılır.
 */
function ConsumptionRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <FleetPage title={t("menu.fleetConsumption")} description={t("fleet.consumption.description")}>
      <ConsumptionBody />
    </FleetPage>
  );
}

function ConsumptionBody(): ReactElement {
  const { t } = useTranslation();
  const initial = defaultConsumptionRange(toIstanbulDate(new Date()));
  const [from, setFrom] = useState<string>(initial.from);
  const [to, setTo] = useState<string>(initial.to);
  const [counterpartyId, setCounterpartyId] = useState("");
  const accounts = useFleetAccounts({ status: "ACTIVE" });
  const valid = from !== "" && to !== "" && from <= to;
  const report = useFleetConsumption({
    ...(valid ? { from, to } : initial),
    counterpartyId: counterpartyId || undefined,
  });
  const masked = report.data?.personalDataMasked ?? false;

  const columns = useMemo<DataTableColumn<Row>[]>(
    () => [
      {
        id: "plate",
        header: t("fleet.fields.plate"),
        hideable: false,
        exportValue: (row) => row.plate,
        cell: (row) => (
          <PersonalValue
            value={row.plate}
            masked={masked}
            format={formatPlateDisplay}
            className="font-mono"
          />
        ),
      },
      {
        id: "counterparty",
        header: t("fleet.fields.counterparty"),
        exportValue: (row) => row.counterpartyName,
        cell: (row) => (
          <Link
            to="/filo/$counterpartyId"
            params={{ counterpartyId: row.counterpartyId }}
            className="underline underline-offset-4"
          >
            {row.counterpartyName}
          </Link>
        ),
      },
      {
        id: "litres",
        header: t("fleet.fields.litres"),
        align: "right",
        exportValue: (row) => row.litres,
        cell: (row) => <QuantityText value={row.litres} unit="LITRE" />,
      },
      {
        id: "amount",
        header: t("fleet.fields.amount"),
        align: "right",
        exportValue: (row) => moneyExport(row.amount),
        cell: (row) => <MoneyText value={row.amount} />,
      },
      {
        id: "count",
        header: t("fleet.fields.transactions"),
        align: "right",
        exportValue: (row) => row.transactionCount,
        cell: (row) => <span className="tabular">{formatCount(row.transactionCount)}</span>,
      },
      {
        id: "unitPrice",
        header: t("fleet.fields.averageUnitPrice"),
        align: "right",
        exportValue: (row) => row.averageUnitPrice,
        cell: (row) => (
          <span className="tabular whitespace-nowrap">{formatUnitPrice(row.averageUnitPrice)}</span>
        ),
      },
      {
        id: "distance",
        header: t("fleet.fields.distance"),
        align: "right",
        exportValue: (row) => row.distanceKm,
        cell: (row) =>
          row.distanceKm === null ? "—" : <span className="tabular">{formatCount(row.distanceKm)} km</span>,
      },
      {
        id: "consumption",
        header: t("fleet.fields.consumption"),
        align: "right",
        exportValue: (row) => row.consumptionPer100Km,
        cell: (row) => (
          <span className="tabular whitespace-nowrap">{consumptionText(row.consumptionPer100Km) ?? "—"}</span>
        ),
      },
    ],
    [t, masked],
  );

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("fleet.fields.counterparty")} className="w-64">
          {(control) => (
            <Select
              {...control}
              value={counterpartyId}
              onChange={(event) => setCounterpartyId(event.target.value)}
            >
              <option value="">{t("common.all")}</option>
              {(accounts.data?.items ?? []).map((item) => (
                <option key={item.counterpartyId} value={item.counterpartyId}>
                  {item.counterpartyCode} {item.counterpartyName}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("fleet.fields.from")} className="w-40">
          {(control) => (
            <DateInput {...control} value={from} onValueChange={(value) => setFrom(value ?? "")} />
          )}
        </FormField>
        <FormField
          label={t("fleet.fields.to")}
          className="w-40"
          error={from !== "" && to !== "" && from > to ? t("fleet.statement.rangeError") : undefined}
        >
          {(control) => <DateInput {...control} value={to} onValueChange={(value) => setTo(value ?? "")} />}
        </FormField>
      </Card>
      <MaskedNotice masked={masked} />
      {report.error ? <Alert tone="danger">{errorMessage(report.error)}</Alert> : null}
      {report.isPending ? (
        <SkeletonRows rows={6} />
      ) : report.data ? (
        <>
          <Card className="p-4">
            <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label={t("fleet.consumption.totalLitres")}>
                <QuantityText value={report.data.totals.litres} unit="LITRE" />
              </Field>
              <Field label={t("fleet.consumption.totalAmount")}>
                <MoneyText value={report.data.totals.amount} />
              </Field>
              <Field label={t("fleet.fields.transactions")}>
                {formatCount(report.data.totals.transactionCount)}
              </Field>
              <Field label={t("fleet.consumption.withoutVehicle")}>
                <QuantityText value={report.data.withoutVehicle.litres} unit="LITRE" /> ·{" "}
                <MoneyText value={report.data.withoutVehicle.amount} />
              </Field>
            </dl>
          </Card>
          <DataTable
            caption={t("fleet.consumption.caption")}
            columns={columns}
            rows={report.data.items}
            rowKey={(row) => row.vehicleId}
            emptyState={
              <EmptyState
                headingLevel={2}
                icon={<FileSpreadsheet />}
                title={t("fleet.consumption.emptyTitle")}
                description={t("fleet.consumption.emptyDescription")}
              />
            }
            onExport={async (visible) =>
              exportRowsToExcel({
                fileName: `filo-tuketim-${report.data!.from}-${report.data!.to}`,
                sheetName: t("menu.fleetConsumption"),
                columns: visible,
                rows: report.data!.items,
              })
            }
          />
          <p className="text-xs text-muted-foreground">{t("fleet.statement.consumptionHint")}</p>
        </>
      ) : null}
    </>
  );
}
